import {
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  effect,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  BaseCssProperties,
  CountryOption,
  FormFieldConfig,
  InputLabelOptions,
  LayoutWidgetConfig,
  ProviderClass,
  TypographyKeys,
  UiZoneWidgetConfig,
  WidgetFields,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Subject, debounceTime, distinctUntilChanged, finalize, map, of, switchMap, takeUntil } from 'rxjs';
import { AutoSuggestAddressResponseItems } from '../../../../../lib/dtos/address.dto';
import { Country } from '../../../../../lib/dtos/countries.dto';
import { ConfigService } from '../../../../../lib/services/config-service/config-service.service';
import { MetadataService } from '../../../../../lib/services/metadata-service/metadata-service.service';

import { Router } from '@angular/router';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { setCssAttributes } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { Store } from '@ngxs/store';
import { StyleServiceService } from '../../../../../lib/services/style-service/style-service.service';
import { PageConfigurationProvider } from '../../../../zones/page-configuration.provider';
import { ZonePageUrlProvider } from '../../../../zones/zone-url-provider';
import { FormSubmitter } from '../form-submitter/form-submitter.component';
import {
  PARENT_FORM_ROW_GAP,
  RESOLVED_FORM_GROUP_PATH,
} from '@app/finxone-web-frontend/app/lib/utils/di-tokens';
import { getNestedValue } from '@app/finxone-web-frontend/app/lib/utils/nested-object-path.utils';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-address-widget',
  templateUrl: './address-widget.component.html',
  styleUrls: ['./address-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AddressWidgetComponent extends FormSubmitter implements OnInit {
  @ViewChild('countryInput') inputElementWrapper: ElementRef;
  @AutoDestroy override destroy$: Subject<void> = new Subject<void>();

  @Input() isAddressHistoryEntry?: boolean;
  @Input() addressHistoryExtraTemplate?: TemplateRef<{ index: number; entryForm: FormGroup }>;
  @Input() addressHistoryExtraContext?: { index: number; entryForm: FormGroup };

  // When true (and not isAddressHistoryEntry), nests this widget's fields under an "address" key
  // on the parent form instead of adding them as flat sibling controls - for payloads that expect
  // address as a nested object (e.g. members[i].address = {...}) rather than flat fields.
  public wrapInAddressObject = false;

  public labelTypeOptions = InputLabelOptions;
  public widgetConfig: UiZoneWidgetConfig;
  public userProfileData: any;
  public filteredAddress: AutoSuggestAddressResponseItems = [];
  public countriesList: CountryOption[] = [];
  public filteredCountries: CountryOption[] = [];
  public selectedCountry: CountryOption = {
    name: '',
    dial_code: '',
    code: '',
  };
  public searchAddressFieldValue = '';
  public addressForm: FormGroup;
  public isError = false;
  public widgetsAttributes: LayoutWidgetConfig[] = [];
  public colorTextAlignStyle: baseCssConfigStyle;
  public colorStyle: baseCssConfigStyle;
  public borderStyle: baseCssConfigStyle;
  public textInputFontStyle: baseCssConfigStyle;
  public textInputBackgroundColor: baseCssConfigStyle;
  public isWorkflow: boolean;
  public isAddressShowSuccessIcon = false;
  public isAddressShowErrorIcon = false;
  public combinedTextInputStyles: baseCssConfigStyle;
  public combinedContentAndBorderStyles: baseCssConfigStyle;
  public addressInputFieldsStyles: { [key: string]: baseCssConfigStyle } = {};
  public formId: string;
  public textStylingClass: TypographyKeys;
  public borderRadiusClass: string;
  private cssVars: Record<string, string> = {};
  public labelType: InputLabelOptions;
  public isInputOnFocus = false;
  public isSearchAddressInputOnFocus = false;
  public isFormValid = false;
  public isAllowedManualEdit: boolean;
  public disableAutocomplete = false;
  public isSearchingAddress = false;
  private searchQuery$ = new Subject<string>();

  private metadataService = inject(MetadataService);
  private configService = inject(ConfigService);
  public dataIn = inject<ProviderClass<any>>(ProviderClass);
  protected _router = inject(Router);
  private styleService = inject(StyleServiceService);
  public pageConfig = inject(PageConfigurationProvider);
  private workflowFormsService = inject(WorkflowFormsService);
  private zonePageUrlProvider = inject(ZonePageUrlProvider);
  public parentFormWidgetRowGap: string | undefined | null = inject(PARENT_FORM_ROW_GAP, { optional: true });
  private readonly resolvedFormGroupPath = inject(RESOLVED_FORM_GROUP_PATH, { optional: true });

  // Injecting Store via super() to avoid conflicts with the parent class FormSubmitter which also injects Store
  constructor() {
    super(inject(Store));
    effect(() => {
      const globalCssVariables = this.configService.globalCssVariables;
      if (globalCssVariables) {
        this.cssVars = globalCssVariables();
        //get the label type and text style class from root styles
        this.labelType = this.getCssVariableValue('--inputFieldLabelType') as InputLabelOptions;
        this.textStylingClass = this.getCssVariableValue('--inputFieldTextStylingClass') as TypographyKeys;
        this.borderRadiusClass = this.getCssVariableValue('--inputFieldBorderRadiusClass');
      }
    });
  }

  ngOnInit(): void {
    if (this.dataIn != undefined) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.attri = this.dataIn.attri;
      this.disableAutocomplete =
        this.widgetConfig.attributes.widgetProperties?.['disableAutocomplete'] ?? false;
      this.userProfileData = this.store.selectSnapshot(ProfileState.getProfile);

      this.isAddressHistoryEntry =
        this.widgetConfig.attributes.widgetProperties?.['isAddressHistoryEntry'] ?? false;
      this.wrapInAddressObject =
        this.widgetConfig.attributes['elementsConfig']?.find(
          (item) => item.field_name === 'wrapInAddressObject',
        )?.show ?? false;

      this.isAllowedManualEdit =
        this.widgetConfig.attributes['elementsConfig']?.find(
          (item) => item.field_name === 'isAllowedManualEdit',
        )?.show ?? true;
    }
    this.formId = this.dataIn?.attri?.widgetProperties?.parentForm;
    this.getCountriesList();

    this.isWorkflow = this.configService.getActiveConfigType().isWorkflow;
    this.buildForm();
    this.setCssAttributes();

    if (this.formId) {
      const form = this.workflowFormsService.getFormPart(this.formId);
      if (form || this.addressForm) {
        this.addressForm.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
          this.updateAddressValidity();
        });
      }
    }

    this.onLoadSearchQuery();
  }

  private onLoadSearchQuery() {
    this.searchQuery$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => {
          if (query.length < 4) {
            this.isSearchingAddress = false;
            return of([]);
          }
          this.isSearchingAddress = true;
          return this.metadataService
            .autosuggestAddress(query)
            .pipe(finalize(() => (this.isSearchingAddress = false)));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        this.filteredAddress = result;
      });
  }

  private updateAddressValidity(): void {
    if (!this.widgetConfig?.attributes?.['field_attributes']) {
      this.isError = false;
      return;
    }

    // Get all required fields from your widget config
    const requiredFields = this.widgetConfig.attributes['field_attributes']
      .filter((field: any) => field.decision_config?.required)
      .map((field: any) => field.name);

    // Check validity for all required fields
    const allValid = requiredFields.every((fieldName: string) => {
      const control = this.addressForm.get(fieldName);
      return control?.valid;
    });

    // Check if any required field is invalid and touched
    const anyInvalidField = requiredFields.some((fieldName: string) => {
      const control = this.addressForm.get(fieldName);
      return control?.invalid && control?.dirty;
    });

    this.isError = anyInvalidField;

    this.checkAddressSuccessErrorIconFlag();
  }

  public get searchAddressFieldAttribute() {
    return this.widgetConfig.attributes['field_attributes'].find(
      (field: WidgetFields) => field.type === 'search_address',
    );
  }

  // field.name is a shared config literal (e.g. "search_address", "country"), so every rendered
  // instance would otherwise emit the same DOM id/for - which breaks label clicks and focus when
  // multiple address entries are on the same page (e.g. address history). Suffix with the
  // per-entry index already passed down via addressHistoryExtraContext to keep ids unique.
  public fieldId(name: string): string {
    const index = this.addressHistoryExtraContext?.index;
    return index != null ? `${name}-${index}` : name;
  }

  private checkAddressSuccessErrorIconFlag() {
    if (this.dataIn && this.widgetConfig) {
      const showSuccessErrorIcon = this.isShowAndErorIcon(this.widgetConfig.attributes['elementsConfig']);
      this.isAddressShowErrorIcon = showSuccessErrorIcon.isShowErrorIcon;
      this.isAddressShowSuccessIcon = showSuccessErrorIcon.isShowSuccessIcon;
    }
  }

  public setCssAttributes() {
    const { contentStyle, colorStyle, borderStyle, textInputFontStyle, textInputBackgroundColor } =
      setCssAttributes(this.styleService, this.configService, this.attri);

    this.combinedContentAndBorderStyles = {
      ...contentStyle,
      ...borderStyle,
    };
    this.contentStyle = contentStyle;
    this.colorTextAlignStyle = {
      ...this.configService.getSpecificBaseCssStyle(this.attri, [
        BaseCssProperties.TEXT_BOLD,
        BaseCssProperties.TEXT_ITALIC,
        BaseCssProperties.TEXT_UNDERLINE,
        BaseCssProperties.FONT_SIZE,
        BaseCssProperties.TEXT_FONT_STYLE,
        BaseCssProperties.TEXTALIGN,
        BaseCssProperties.COLOR,
      ]),
    };
    this.colorStyle = colorStyle;
    this.borderStyle = borderStyle;
    this.textInputFontStyle = textInputFontStyle;
    this.textInputBackgroundColor = textInputBackgroundColor;
  }

  public getCountriesList() {
    this.configService
      .getCountries()
      .pipe(
        takeUntil(this.destroy$),
        map((countries: Country[]) => {
          this.countriesList = countries;
          if (this.userProfileData?.country) {
            const country = this.countriesList.find(
              (x: CountryOption) => x.code == this.userProfileData.country,
            );
            if (country) {
              this.selectedCountry = country;
              this.addressForm?.get('country')?.setValue(country.code);
            }
          }
        }),
      )
      .subscribe();
  }

  private buildForm() {
    if (!this.formId) return;

    const formConfig: FormFieldConfig[] = (this.widgetConfig.attributes['field_attributes'] || []).map(
      (field: any) => {
        return {
          id: field.name,
          type: field.type,
          label: field.label || field.name,
          parentForm: this.formId,
          name: field.name,
          validation: {
            required: field.decision_config?.required ?? false,
          },
        };
      },
    );

    if (this.isAddressHistoryEntry) {
      // In address history context, this.formId is the fully-resolved entry path
      // (e.g. "onboarding.customForm-page_10.addressList.0"). The entry FormGroup
      // pre-creates an "address" sub-group; populate it directly instead of calling
      // generateCustomFormId, which would double-append the page suffix and create
      // a spurious "customForm-page_X" key inside the entry.
      const addressSubPath = `${this.formId}.address`;
      const form = this.workflowFormsService.getFormPart(addressSubPath) ?? new FormGroup({});
      this.addressForm = form;
      this.workflowFormsService.addFormControl(this.addressForm, formConfig);
    } else {
      // A 'group'/'array' typed Form Widget ancestor resolves its own nested path and hands it
      // down here - otherwise fall back to computing a plain page-scoped path from parentForm.
      const parentFormId =
        this.resolvedFormGroupPath ??
        this.workflowFormsService.generateCustomFormId(this.formId, this.zonePageUrlProvider);

      if (this.wrapInAddressObject) {
        // Nest under an "address" sub-group instead of adding flat sibling controls - for
        // payloads that expect address as a nested object (e.g. members[i].address).
        const parentGroup = this.workflowFormsService.getFormPart(parentFormId) as FormGroup | undefined;
        if (parentGroup && !parentGroup.contains('address')) {
          parentGroup.addControl('address', new FormGroup({}));
        }
        const addressSubPath = `${parentFormId}.address`;
        this.addressForm = this.workflowFormsService.getFormPart(addressSubPath) ?? new FormGroup({});
        this.workflowFormsService.addFormControl(this.addressForm, formConfig);
      } else {
        this.addressForm = this.workflowFormsService.getFormPart(parentFormId) ?? new FormGroup({});
        this.workflowFormsService.addFormControl(this.addressForm, formConfig);
      }
    }
    const stateValues =
      this.store.selectSnapshot(FormActionState.getFormActionStateWithId(this.formId)) ?? {};
    this.onCountryInputChange(this.selectedCountry.code || null);

    const scopedFormData = this.getScopedFormData(stateValues?.formData);
    if (scopedFormData) {
      this.patchFormValues(scopedFormData);
      this.updateAddressValidity();
    }
  }

  /**
   * Resolves which slice of this.formId's flat formData this widget's fields should hydrate
   * from. For a plain root usage that's the top-level formData itself (unchanged). When nested
   * under a 'group'/'array' typed Form Widget ancestor (resolvedFormGroupPath present), the real
   * saved values live at a nested path instead (e.g. members[2].address) rather than flat
   * top-level keys - patchFormValues would otherwise silently find nothing there and reset fields.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getScopedFormData(formData: any): any {
    if (!formData || !this.resolvedFormGroupPath) return formData;

    const prefix = `${this.workflowFormsService.generateCustomFormId(this.formId, this.zonePageUrlProvider)}.`;
    if (!this.resolvedFormGroupPath.startsWith(prefix)) return formData;

    const relativePath = this.resolvedFormGroupPath.slice(prefix.length);
    const itemData = getNestedValue<Record<string, unknown>>(formData, relativePath);
    return this.wrapInAddressObject ? itemData?.['address'] : itemData;
  }

  private patchFormValues(formData: Record<string, string>) {
    this.addressForm.get('country')?.setValue(formData?.['country']);
    this.addressForm.get('county')?.setValue(formData?.['county']);
    this.addressForm.get('address1')?.setValue(formData?.['address1']);
    this.addressForm.get('address2')?.setValue(formData?.['address2']);
    this.addressForm.get('address3')?.setValue(formData?.['address3']);
    this.addressForm.get('address4')?.setValue(formData?.['address4']);
    this.addressForm.get('city')?.setValue(formData?.['city']);
    this.addressForm.get('postalCode')?.setValue(formData?.['postalCode']);
    const formValues: Record<string, string> = this.addressForm.value;
    let name = '';
    Object.values(formValues).forEach((value: string) => {
      name = name + value + ' ';
    });
    const country = this.countriesList.find((x: CountryOption) => x.code == formData?.['country']);
    if (country) {
      this.selectedCountry = country;
      this.addressForm.get('country')?.setValue(country.code);
    }
    this.isError = false;
  }

  public stripAsterisks(label: string): string {
    return label.replace(/\*/g, '').trim();
  }

  public getLocation(query: string) {
    this.searchQuery$.next(query);
  }

  public onSearchAddressChange(search: string): void {
    this.searchAddressFieldValue = search;
    this.getLocation(search);
  }

  public onAddressOptionSelected(addressId: string | null): void {
    if (addressId) {
      this.getAddressByLocation(addressId);
    }
  }

  public getAddressByLocation(addressId: string) {
    this.metadataService
      .getAddressById(addressId)
      .pipe(
        debounceTime(400),
        takeUntil(this.destroy$),
        map((response) => {
          this.selectedCountry = {
            name: response.country,
            code: 'GB',
            dial_code: '+44',
          };
          response.country = 'GB';
          this.addressForm.patchValue({
            address1: response.line_1,
            address2: response.line_2,
            address3: response.line_3,
            address4: response.line_4,
            city: response.city,
            county: response.county,
            postalCode: response.postcode,
            country: response.country,
          });
          this.addressForm.enable(); //make sure the fields are enabled
        }),
      )
      .subscribe();
  }

  public filterCountries(query: string) {
    const filtered: any[] = [];
    for (const element of this.countriesList) {
      const type = element;
      if (type.name.toLowerCase().indexOf(query.toLowerCase()) == 0) {
        filtered.push(type);
      }
    }
    this.filteredCountries = filtered;

    const exactMatch = this.countriesList.find(
      (country) => country.name.toLowerCase() === query.toLowerCase(),
    );
    this.onCountryInputChange(exactMatch?.code ?? null);
  }

  public onCountryInputChange(code: string | null) {
    const country = code ? this.countriesList.find((option) => option.code === code) : null;
    this.selectedCountry = country ?? { name: '', code: '', dial_code: '' };
    this.addressForm.get('country')?.setValue(this.selectedCountry.code);
  }

  private getCssVariableValue(variableName: string): string {
    return this.cssVars[variableName];
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  onTouched: Function = () => {};

  public handleLabelClick(): void {
    if (this.labelType === this.labelTypeOptions.FLOATING) {
      this.inputElementWrapper?.nativeElement.querySelector('input')?.focus();
    }
  }
}
